import apiClient from "./client";
import {
  IUserActionLog,
  CreateActionLogDto,
  ActionLogQueryDto,
  ActionLogAction,
  ActionLogCategory,
  ActionLogLevel,
} from "shared";

export interface PaginatedActionLogs {
  logs: IUserActionLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Action Logs API
export const actionLogsApi = {
  async getLogs(query?: ActionLogQueryDto): Promise<PaginatedActionLogs> {
    const params = new URLSearchParams();

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const { data } = await apiClient.get<PaginatedActionLogs>(`/user-action-logs?${params.toString()}`);
    return data;
  },

  async getLogById(id: string): Promise<IUserActionLog> {
    const { data } = await apiClient.get<IUserActionLog>(`/user-action-logs/${id}`);
    return data;
  },

  async createLog(log: CreateActionLogDto): Promise<IUserActionLog> {
    const { data } = await apiClient.post<IUserActionLog>("/user-action-logs", log);
    return data;
  },

  async exportLogs(query?: ActionLogQueryDto): Promise<Blob> {
    const params = new URLSearchParams();

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const { data } = await apiClient.get(`/user-action-logs/export/csv?${params.toString()}`, {
      responseType: 'blob',
    });
    return data;
  },

  // Get action log statistics
  async getStatistics(): Promise<{
    totalLogs: number;
    logsByCategory: Record<string, number>;
    logsByLevel: Record<string, number>;
    logsByAction: Record<string, number>;
    recentActivity: IUserActionLog[];
  }> {
    const { data } = await apiClient.get('/user-action-logs/statistics');
    return data;
  }
};

// Constants for UI
export const ACTION_LABELS: Record<ActionLogAction, string> = {
  [ActionLogAction.CREATE]: 'Create',
  [ActionLogAction.UPDATE]: 'Update',
  [ActionLogAction.DELETE]: 'Delete',
  [ActionLogAction.VIEW]: 'View',
  [ActionLogAction.LOGIN]: 'Login',
  [ActionLogAction.LOGOUT]: 'Logout',
  [ActionLogAction.EXPORT]: 'Export',
  [ActionLogAction.PRINT]: 'Print',
  [ActionLogAction.BULK_CREATE]: 'Bulk Create',
  [ActionLogAction.BULK_UPDATE]: 'Bulk Update',
  [ActionLogAction.BULK_DELETE]: 'Bulk Delete',
  [ActionLogAction.RESTORE]: 'Restore',
  [ActionLogAction.MARK_PAID]: 'Mark Paid',
  [ActionLogAction.MARK_UNPAID]: 'Mark Unpaid',
  [ActionLogAction.SEARCH]: 'Search',
};

export const CATEGORY_LABELS: Record<ActionLogCategory, string> = {
  [ActionLogCategory.AUTH]: 'Authentication',
  [ActionLogCategory.ORDER]: 'Order',
  [ActionLogCategory.CUSTOMER]: 'Customer',
  [ActionLogCategory.INVENTORY]: 'Inventory',
  [ActionLogCategory.MENU]: 'Menu',
  [ActionLogCategory.SYSTEM]: 'System',
  [ActionLogCategory.FINANCIAL]: 'Financial',
  [ActionLogCategory.USER_MANAGEMENT]: 'User Management',
  [ActionLogCategory.DATA_EXPORT]: 'Data Export',
  [ActionLogCategory.BULK_OPERATIONS]: 'Bulk Operations',
};

export const LEVEL_LABELS: Record<ActionLogLevel, string> = {
  [ActionLogLevel.LOW]: 'Low',
  [ActionLogLevel.MEDIUM]: 'Medium',
  [ActionLogLevel.HIGH]: 'High',
  [ActionLogLevel.CRITICAL]: 'Critical',
};

export const LEVEL_COLORS: Record<ActionLogLevel, string> = {
  [ActionLogLevel.LOW]: '#10b981', // green
  [ActionLogLevel.MEDIUM]: '#f59e0b', // amber
  [ActionLogLevel.HIGH]: '#ef4444', // red
  [ActionLogLevel.CRITICAL]: '#dc2626', // dark red
};